import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { humanizeKey } from '../constants/permissions';
import { isUsageLimitError, usageLimitMessage } from '../utils/planErrors';
import { looksLikeHtml, SERVER_UNAVAILABLE_MESSAGE, unavailableMessage } from '../utils/serverDownMessage';

interface ToastContextType {
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    /** Non-error caution, e.g. "saved, but the ledger entry was not posted". */
    showWarning: (message: string) => void;
    showApiError: (error: any, fallback: string) => void;
}

const ToastContext = createContext<ToastContextType>({} as ToastContextType);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<'success' | 'error' | 'warning'>('success');

    const showSuccess = useCallback((message: string) => {
        setMessage(message);
        setSeverity('success');
        setOpen(true);
    }, []);

    const showError = useCallback((message: string) => {
        // Callers that pass the response body straight through must never paint a proxy's HTML page.
        setMessage(typeof message === 'string' && looksLikeHtml(message) ? SERVER_UNAVAILABLE_MESSAGE : message);
        setSeverity('error');
        setOpen(true);
    }, []);

    const showWarning = useCallback((message: string) => {
        setMessage(message);
        setSeverity('warning');
        setOpen(true);
    }, []);

    const showApiError = useCallback((error: any, fallback: string) => {
        const down = unavailableMessage(error);
        if (down) {
            setMessage(down);
            setSeverity('error');
            setOpen(true);
            return;
        }
        const missing = error?.response?.status === 403 ? error?.response?.data?.missingPermission : undefined;
        if (typeof missing === 'string') {
            setMessage(`You don't have permission to do this (${humanizeKey(missing)}). Ask an admin to add it to your role.`);
            setSeverity('error');
            setOpen(true);
            return;
        }
        // Merge (develop -> dynamic roles): develop's free-tier cap answers with a JSON OBJECT
        // ({ code: 'FREE_TIER_LIMIT', message, cap, current }), and this branch's permission denial
        // answers with another one ({ error, missingPermission }, handled above). Reading only
        // `typeof data === 'string'` silently dropped every object body onto the generic fallback,
        // so the seat/module/device cap doors lost their upgrade text everywhere except the two
        // screens that special-case it. Fall back to the body's own `message` for any object shape
        // before giving up on the caller's fallback string.
        if (isUsageLimitError(error)) {
            setMessage(usageLimitMessage(error));
            setSeverity('error');
            setOpen(true);
            return;
        }
        const apiMessage = error?.response?.data;
        const objectMessage = apiMessage && typeof apiMessage === 'object'
            ? [apiMessage.message, apiMessage.error].find(
                (candidate: unknown): candidate is string => typeof candidate === 'string' && candidate.length > 0)
            : undefined;
        const text = (typeof apiMessage === 'string' && apiMessage) || objectMessage || fallback;
        setMessage(text);
        setSeverity('error');
        setOpen(true);
    }, []);

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    // Stable identity: consumers list `toast` in effect/callback deps, so a fresh object on every
    // toast open/close would re-run their data fetches (and loop when the fetch itself toasts).
    const value = useMemo(() => ({ showSuccess, showError, showWarning, showApiError }), [showSuccess, showError, showWarning, showApiError]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
};

export const useToast = () => useContext(ToastContext);
